export const ANALYTICS_CONSENT_KEY = "alphasearch.analytics-cookie-consent.v2";

const DENIED_CONSENT = {
  analytics_storage: "denied",
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
};

const STATIC_PATHS = new Set([
  "/", "/login", "/signup", "/page", "/password/forgot", "/verify-email/check", "/verify-email/resend",
  "/thanks", "/help/hubspot", "/support", "/onboarding", "/landing",
  "/change-password", "/lists/companies", "/company", "/conferences", "/admin",
  "/settings/account", "/settings/subscription", "/settings/adjust-plan",
  "/settings/usage", "/settings/apikeys", "/settings/integrations",
  "/workspaces", "/chat", "/chats", "/credits", "/help",
]);
const RECORD_PATHS = [
  [/^\/workspaces\/[^/]+$/, "/workspaces/:id"],
  [/^\/lists\/companies\/[^/]+$/, "/lists/companies/:searchKey"],
  [/^\/company\/[^/]+$/, "/company/:stagedId"],
  [/^\/conference\/[^/]+$/, "/conference/:conferenceId"],
  [/^\/verify-email\/[^/]+$/, "/verify-email/:token"],
  [/^\/password\/reset\/[^/]+$/, "/password/reset/:authCode"],
  [/^\/signup\/[^/]+$/, "/signup/:inviteCode"],
];

export function validMeasurementId(value) {
  return typeof value === "string" && /^G-[A-Z0-9]+$/.test(value) ? value : "";
}

// Count every page without sending query strings, fragments or dynamic path values.
export function analyticsPath(pathname) {
  const path = pathname.split(/[?#]/)[0].replace(/\/+$/, "") || "/";
  if (STATIC_PATHS.has(path)) return path;
  return RECORD_PATHS.find(([pattern]) => pattern.test(path))?.[1] || "/not-found";
}

export function readAnalyticsConsent() {
  try {
    const value = window.localStorage.getItem(ANALYTICS_CONSENT_KEY);
    return ["accepted", "rejected"].includes(value) ? value : null;
  } catch {
    return null;
  }
}

export function saveAnalyticsConsent(value) {
  try {
    window.localStorage.setItem(ANALYTICS_CONSENT_KEY, value);
  } catch {
    // The choice still applies in memory when browser storage is unavailable.
  }
}

export function createPageViewTracker(browser = window) {
  let stream = "";
  let script = null;
  let loaded = false;
  let configured = false;
  let initialized = false;
  let cookieConsent = "denied";
  let appliedConsent = "denied";
  let pending = [];
  let lastPath = null;
  let previousLocation = "";

  function gtag() {
    browser.dataLayer = browser.dataLayer || [];
    browser.dataLayer.push(arguments);
  }

  function disable() {
    if (stream) browser[`ga-disable-${stream}`] = true;
    setConsent(null);
    pending = [];
    previousLocation = "";
  }

  function applyConsent(value) {
    if (!initialized || appliedConsent === value) return;
    gtag("consent", "update", {...DENIED_CONSENT, analytics_storage: value});
    appliedConsent = value;
  }

  function setConsent(choice) {
    cookieConsent = choice === "accepted" ? "granted" : "denied";
    applyConsent(cookieConsent);
  }

  function initializeConsent() {
    if (initialized) return;
    // Queue denied defaults before loading any Google code or measurement commands.
    gtag("consent", "default", {...DENIED_CONSENT});
    gtag("set", "ads_data_redaction", true);
    gtag("set", "url_passthrough", false);
    initialized = true;
  }

  function pageContext(path) {
    return {
      page_location: `${browser.location.origin}${path}`,
      page_referrer: previousLocation,
      page_title: "AlphaSearch",
    };
  }

  function sendPage({pathname, path}) {
    if (lastPath === pathname) return;
    const page = pageContext(path);
    // Override defaults before config so automatic session events also use safe context.
    gtag("set", page);
    if (!configured) {
      gtag("js", new Date());
      gtag("config", stream, {
        ...page,
        send_page_view: false,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      });
      configured = true;
    }
    gtag("event", "page_view", {...page, send_to: stream});
    lastPath = pathname;
    previousLocation = page.page_location;
  }

  function sendPages() {
    if (!loaded || !pending.length) return;
    // Never identify pages that were visited before acceptance, or after withdrawal.
    // A mixed loading batch is conservatively sent entirely without analytics cookies.
    const allowCookies = cookieConsent === "granted" && pending.every((page) => page.allowCookies);
    applyConsent(allowCookies ? "granted" : "denied");
    while (pending.length) sendPage(pending.shift());
    applyConsent(cookieConsent);
  }

  function track(measurementId, pathname, choice = null) {
    const id = validMeasurementId(measurementId);
    const path = analyticsPath(pathname);
    if (!id) {
      disable();
      return;
    }
    if (stream && stream !== id) {
      disable();
      lastPath = null;
      script?.remove();
      script = null;
      loaded = false;
      configured = false;
    }
    stream = id;
    browser[`ga-disable-${stream}`] = false;
    initializeConsent();
    // Consent pings must also see sanitized context before Google code can run.
    gtag("set", pageContext(path));
    setConsent(choice);
    if ((pending[pending.length - 1]?.pathname || lastPath) !== pathname) {
      pending.push({pathname, path, allowCookies: cookieConsent === "granted"});
    }
    if (!script) {
      browser.dataLayer = browser.dataLayer || [];
      const tag = browser.document.createElement("script");
      script = tag;
      tag.async = true;
      tag.referrerPolicy = "no-referrer";
      tag.src = `https://www.googletagmanager.com/gtag/js?id=${stream}`;
      tag.onload = () => {
        if (script !== tag) return;
        loaded = true;
        sendPages();
      };
      tag.onerror = () => {
        if (script !== tag) return;
        disable();
        tag.remove();
        script = null;
        loaded = false;
      };
      browser.document.head.appendChild(tag);
    }
    sendPages();
  }

  return {track, setConsent, disable};
}
